import { useEffect, useRef, useSyncExternalStore } from "react";
import {
  getAccessToken,
  subscribeAccessToken,
} from "../../lib/api-client";

const ACCESS_TOKEN_PROTOCOL = "family-gifts.access-token";
const MAX_RECONNECT_DELAY_MS = 30_000;
const INVALIDATION_EVENT = "family-gifts:reservation-invalidation";

export interface ReservationInvalidation {
  type: "wish.status.changed";
  wish_id: string;
  status: "free" | "reserved" | "purchased";
  occurred_at: string;
}

export type ReservationInvalidationSignal =
  | { reason: "event"; event: ReservationInvalidation }
  | { reason: "reconnect" };

function websocketURL(): string {
  const apiURL = import.meta.env.VITE_API_URL || "/api";
  const url = new URL(`${apiURL.replace(/\/$/, "")}/ws`, window.location.href);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
}

export function reconnectDelay(attempt: number, random = Math.random): number {
  const exponential = Math.min(1_000 * 2 ** attempt, MAX_RECONNECT_DELAY_MS);
  return Math.min(
    MAX_RECONNECT_DELAY_MS,
    Math.round(exponential * (0.5 + random())),
  );
}

function parseInvalidation(data: unknown): ReservationInvalidation | null {
  if (typeof data !== "string") return null;
  try {
    const value = JSON.parse(data) as Partial<ReservationInvalidation>;
    if (
      value.type !== "wish.status.changed" ||
      typeof value.wish_id !== "string" ||
      !["free", "reserved", "purchased"].includes(value.status ?? "") ||
      typeof value.occurred_at !== "string"
    ) {
      return null;
    }
    return {
      type: value.type,
      wish_id: value.wish_id,
      status: value.status as ReservationInvalidation["status"],
      occurred_at: value.occurred_at,
    };
  } catch {
    return null;
  }
}

function emitInvalidation(signal: ReservationInvalidationSignal): void {
  window.dispatchEvent(
    new CustomEvent<ReservationInvalidationSignal>(INVALIDATION_EVENT, {
      detail: signal,
    }),
  );
}

export function useReservationInvalidation(
  listener: (signal: ReservationInvalidationSignal) => void,
  enabled = true,
): void {
  const listenerRef = useRef(listener);
  listenerRef.current = listener;

  useEffect(() => {
    if (!enabled) return;
    const handle = (event: Event) => {
      listenerRef.current(
        (event as CustomEvent<ReservationInvalidationSignal>).detail,
      );
    };
    window.addEventListener(INVALIDATION_EVENT, handle);
    return () => window.removeEventListener(INVALIDATION_EVENT, handle);
  }, [enabled]);
}

export function useFamilyWebSocket(): void {
  const token = useSyncExternalStore(
    subscribeAccessToken,
    getAccessToken,
    () => null,
  );
  const hasConnected = useRef(false);

  useEffect(() => {
    if (!token) return;

    let stopped = false;
    let socket: WebSocket | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;
    let recoveryNeeded = !navigator.onLine;

    const clearRetry = () => {
      if (retryTimer !== null) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
    };

    const connect = () => {
      if (stopped || !navigator.onLine || socket !== null) return;
      const current = new WebSocket(websocketURL(), [
        ACCESS_TOKEN_PROTOCOL,
        token,
      ]);
      socket = current;

      current.onopen = () => {
        if (stopped || socket !== current) return;
        attempt = 0;
        if (hasConnected.current || recoveryNeeded) {
          emitInvalidation({ reason: "reconnect" });
        } else {
          hasConnected.current = true;
        }
        recoveryNeeded = false;
      };
      current.onmessage = (message) => {
        const event = parseInvalidation(message.data);
        if (event) emitInvalidation({ reason: "event", event });
      };
      current.onclose = () => {
        if (socket === current) socket = null;
        recoveryNeeded = true;
        if (stopped || !navigator.onLine || retryTimer !== null) return;
        const delay = reconnectDelay(attempt++);
        retryTimer = setTimeout(() => {
          retryTimer = null;
          connect();
        }, delay);
      };
    };

    const handleOffline = () => {
      recoveryNeeded = true;
      clearRetry();
      socket?.close();
      socket = null;
    };
    const handleOnline = () => connect();

    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);
    connect();

    return () => {
      stopped = true;
      clearRetry();
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
      socket?.close();
      socket = null;
    };
  }, [token]);
}
